import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import { routeList } from "../../App";
import { lang } from "../../assets/languages/en.language";
import { LocationFilledIcon } from "../../components/svgs/icons/Location.filled.icon";
import { theme } from "../../configs/theme";
import { usePwaViewportWidth } from "../../hooks/useScreenWidth";
import api from "../../services/api";
import { getVpFontSize } from "../../services/utils";
import { useUserStore } from "../../store/useUserStore";
import { PurpleButton, SwitchButton } from "../../styles/Button.style";
import { MainContainer, RootContainer } from "../../styles/Container.style";
import { BigTitle, Paragraph, Title } from "../../styles/Text";
import { Gap, Row } from "../../styles/Utils";

export default function DistancePage() {
  const navigate = useNavigate();
  const {
    profile: { distance: initialDistance, isKmUnit: initialIsKmUnit },
    setDistance: setStoreDistance,
  } = useUserStore();
  const minDistance = 1;
  const maxDistance = 100;
  const [distance, setDistance] = useState<number>(initialDistance!);
  const [isKmUnit, setIsKmUnit] = useState<boolean>(initialIsKmUnit);
  const kmUnitLabel = lang.distancePage.distanceUnit.km;
  const mileUnitLabel = lang.distancePage.distanceUnit.mile;
  const distanceUnitLabel = isKmUnit ? kmUnitLabel : mileUnitLabel;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDistance(Number(event.target.value));
  };

  // const navigate = useNavigate();
  const viewportWidth = usePwaViewportWidth();

  const handleSubmission = async () => {
    try {
      await api.updateDistance({ distance, isKmUnit });
      mixpanel.track("Update Distance");
      setStoreDistance(distance, isKmUnit);
      navigate(routeList.settings);
    } catch (err) {
      console.log("Error updating distance", err);
      alert("Error updating distance");
    }
  };

  const handleUnitSwitch = () => {
    setIsKmUnit((prev) => !prev);
  };

  return (
    <RootContainer>
      <BigTitle $fontSize={getVpFontSize(viewportWidth, 13)}>{lang.distancePage.title}</BigTitle>
      <Gap $height={1} />
      <MainContainer>
        <Paragraph>{lang.distancePage.message}</Paragraph>
        <Gap $height={1} />
        <MediumTitle>
          {lang.distancePage.title2}: {distance}
          {distanceUnitLabel}
        </MediumTitle>
        <Gap $height={3} />
        <SliderContainer>
          <Slider min={minDistance} max={maxDistance} value={distance} onChange={handleChange} />
          <ThumbSVG $left={2 + ((distance - 1) * 96) / (maxDistance - 1)}>
            <LocationFilledIcon color={theme.color.tertiary} />
          </ThumbSVG>
        </SliderContainer>
        <Gap $height={1} />
        <Row $justifyContent="center" $gap={0.5} $alignItem="center">
          <Title>{kmUnitLabel}</Title>
          <SwitchButton size={1} checked={!isKmUnit} onClick={handleUnitSwitch} />
          <Title>{mileUnitLabel}</Title>
        </Row>
        <Image src="/illustrations/girl-walk-dog.png" />
      </MainContainer>
      <Gap $height={1.5} />
      <PurpleButton type="submit" onClick={handleSubmission}>
        <span>{lang.buttons.continue}</span>
      </PurpleButton>
    </RootContainer>
  );
}

const Image = styled.img`
  width: 80%;
  transform: translateY(3rem);
`;

const MediumTitle = styled(Title)`
  font-size: ${(props) => props.theme.font.size.title.big};
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
`;

const Slider = styled.input.attrs({ type: "range" })`
  width: 100%;
  appearance: none;
  background: ${(props) => props.theme.color.primary};
  position: relative;
  cursor: pointer;
  border-radius: 100px;
  height: 1rem;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 48px;
    height: 50px;
    /* background: url("/vite.svg");
    transform: translateY(-1rem); */
    transform: translateY(-20px);
    background: transparent;
    border: none; // comment to display the selector
    box-shadow: none; // comment to display the selector
    cursor: pointer;
  }
`;

const ThumbSVG = styled.div<{ $left: number }>`
  position: absolute;
  pointer-events: none;
  left: ${(props) => props.$left}%;
  top: -2.3rem;
  transform: translateX(-50%);
  svg {
    height: 3rem;
  }
`;
